import React, { useEffect, useState, useMemo, useCallback } from "react";
import styles from '../LessonModal.module.css';
import s from "../Lesson.module.css";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import { useAppTranslation } from "./TranslationContext";
import { CustomVideoPlayer } from "./CustomVideoPlayer";

export const Lesson = () => {
  const { user, handleLang, userKursData, setUserKursData } = useAuth(); // ← объединил вызовы useAuth
  const { t, lang } = useAppTranslation();
  const { kursId, id } = useParams();
  const navigate = useNavigate();
  const localUser = JSON.parse(localStorage.getItem('user'));

  const [dataThemes, setThemes] = useState([]);
  const [elements, setElements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [lesson, setLesson] = useState(null);
  const [prevLesson, setPrevLesson] = useState(null);
  const [nextLesson, setNextLesson] = useState(null);

  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [completedByElement, setCompletedByElement] = useState({});

  const [checkpoints, setCheckpoints] = useState([]);
  const [answeredCheckpoints, setAnsweredCheckpoints] = useState({});
  const [userAnswers, setUserAnswers] = useState({});

  // === Загружаем user-kurs (берём tartib и elementtartib) ===
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('https://api.eduskill.uz/api/user-kurs/', {
          headers: token ? { Authorization: `Token ${token}` } : {},
        });
        if (!response.ok) throw new Error('Ошибка при загрузке данных');

        const data = await response.json();
        const foundItem = data.find(item =>
          (parseInt(item.kurs) === parseInt(kursId)) &&
          (parseInt(item.user) === parseInt(localUser.id))
        );

        if (foundItem) {
          setUserKursData({
            id: foundItem.id,
            // раньше: mavzu/element → теперь:
            tartib: Number(foundItem.tartib ?? 0),
            elementtartib: Number(foundItem.elementtartib ?? 0),
            // если у вас в контексте ещё хранятся другие поля — добавьте их сюда
          });
        }
      } catch (error) {
        console.error('Произошла ошибка:', error);
      }
    };
    fetchData();
  }, [kursId, localUser?.id, setUserKursData]);

  // === Загружаем список уроков курса ===
  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const res = await fetch(`https://api.eduskill.uz/api/kurs-mavzulari/?kurs=${kursId}`);
        if (!res.ok) throw new Error("Ошибка сети");
        const data = await res.json();
        const sorted = [...data].sort((a, b) => a.tartib - b.tartib);
        setThemes(sorted);
      } catch (err) {
        console.error(err);
      }
    };
    fetchThemes();
  }, [kursId]);

  // === Загружаем подтемы текущего урока ===
  useEffect(() => {
    const fetchElements = async () => {
      try {
        setLoading(true);
        const res = await fetch(`https://api.eduskill.uz/api/mavzu-elementlari/?kursmavzulari=${id}`);
        if (!res.ok) throw new Error("Ошибка при загрузке подтем");
        const data = await res.json();
        const sorted = [...data].sort((a, b) => a.tartib - b.tartib);
        setElements(sorted);
        setCurrentIndex(0);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchElements();
  }, [id]);

  const current = elements[currentIndex];

  useEffect(() => {
    setAnsweredCheckpoints({});
  }, [current?.id]);

  const handleInputChange = (cpId, value) => {
    setUserAnswers(prev => ({ ...prev, [cpId]: value }));
  };

  const handleAllAnswersSubmit = useCallback(async (e) => {
    e.preventDefault();

    const newAnswered = {};
    let allCorrect = true;

    checkpoints.forEach(cp => {
      const userAnswer = (userAnswers[cp.id] || "").trim().toLowerCase();
      if (userAnswer === cp.answer.toLowerCase()) {
        newAnswered[cp.id] = true;
      } else {
        newAnswered[cp.id] = false;
        allCorrect = false;
      }
    });

    setAnsweredCheckpoints(prev => ({ ...prev, ...newAnswered }));
    if (!allCorrect) return;

    // ✅ Если все правильные → сохраняем прогресс (аналог handleVideoComplete)
    if (!current?.id || !userKursData || !elements?.length) return;

    setCompletedByElement(prev => ({ ...prev, [current.id]: true }));

    const isLastWatched =
      lesson?.tartib === userKursData.tartib &&               // ← заменили mavzu → tartib
      current.tartib === userKursData.elementtartib;          // ← element → elementtartib

    if (isLastWatched) {
      let newTartib = userKursData.tartib;
      let newElementTartib = userKursData.elementtartib;
      const isLastElement = current.tartib === elements.length;

      if (isLastElement) {
        newTartib = userKursData.tartib + 1;
        newElementTartib = 1;
      } else {
        newElementTartib = userKursData.elementtartib + 1;
      }

      const updatedData = { ...userKursData, tartib: newTartib, elementtartib: newElementTartib };

      try {
        const token = localStorage.getItem('token');
        // 1) Локально (если вы этим пользуетесь)
        localStorage.setItem("userKursData", JSON.stringify(updatedData));
        // 2) На сервер
        await fetch(`https://api.eduskill.uz/api/user-kurs/${userKursData.id}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Token ${token}` } : {}),
          },
          body: JSON.stringify({ tartib: newTartib, elementtartib: newElementTartib }),
        });
        // 3) В состоянии
        setUserKursData(updatedData);
      } catch (error) {
        console.error("Ошибка при обновлении прогресса:", error);
      }
    }
  }, [userAnswers, current, checkpoints, userKursData, elements, lesson, setUserKursData]);

  // === Загружаем чекпоинты ===
  useEffect(() => {
    if (!current?.id) return;

    const fetchCheckpoints = async () => {
      try {
        const res = await fetch(`https://api.eduskill.uz/api/element-savol/?element=${current.id}`);
        if (!res.ok) throw new Error("Ошибка при загрузке чекпоинтов");
        const data = await res.json();

        const parseTime = (timeStr) => {
          const [h, m, s] = timeStr.split(":").map(Number);
          return h * 3600 + m * 60 + s;
        };

        const cps = data.map(item => ({
          id: item.id,
          tartib: item.tartib,
          time: item.time ? parseTime(item.time) : null,
          question: handleLang(item.savol_uz, item.savol_ru, item.savol_en),
          answer: handleLang(item.javob_uz, item.javob_ru, item.javob_en),
        }));

        cps.sort((a, b) => a.tartib - b.tartib);
        setCheckpoints(cps);
        setAnsweredCheckpoints({});
      } catch (err) {
        console.error(err);
      }
    };

    fetchCheckpoints();
  }, [current?.id, lang, handleLang]);

  // === Определяем prev/next урок ===
  useEffect(() => {
    if (!dataThemes.length) return;

    let prev = null, next = null;
    const currentLesson = dataThemes.find(item => item.id === parseInt(id));
    setLesson(currentLesson);
    if (currentLesson) {
      prev = dataThemes.find(item => item.tartib === currentLesson.tartib - 1);
      next = dataThemes.find(item => item.tartib === currentLesson.tartib + 1);
    }

    setPrevLesson(prev);
    setNextLesson(next);
  }, [dataThemes, id]);

  // === Доступ по прогрессу: теперь через tartib/elementtartib ===
  const hasProgressAccess = useMemo(() => {
    if (!userKursData || !lesson || !current) return false;
    if (lesson.tartib < userKursData.tartib) return true;
    if (lesson.tartib === userKursData.tartib &&
        current.tartib < userKursData.elementtartib) return true;
    return false;
  }, [userKursData, lesson, current]);

  // === Завершение видео: обновляем tartib/elementtartib ===
  const handleVideoComplete = useCallback(async () => {
    if (!current?.id || !userKursData || !elements?.length) return;
    setCompletedByElement(prev => ({ ...prev, [current.id]: true }));

    const isLastWatched =
      lesson?.tartib === userKursData.tartib &&
      current.tartib === userKursData.elementtartib;

    if (isLastWatched) {
      let newTartib = userKursData.tartib;
      let newElementTartib = userKursData.elementtartib;
      const isLastElement = current.tartib === elements.length;

      if (isLastElement) {
        newTartib = userKursData.tartib + 1;
        newElementTartib = 1;
      } else {
        newElementTartib = userKursData.elementtartib + 1;
      }

      const updatedData = { ...userKursData, tartib: newTartib, elementtartib: newElementTartib };

      try {
        const token = localStorage.getItem('token');
        await fetch(`https://api.eduskill.uz/api/user-kurs/${userKursData.id}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Token ${token}` } : {}),
          },
          body: JSON.stringify({ tartib: newTartib, elementtartib: newElementTartib }),
        });

        setUserKursData(updatedData);
      } catch (error) {
        console.error("Ошибка при обновлении прогресса:", error);
      }
    }
  }, [current, userKursData, elements, lesson, setUserKursData]);

  // === Ответы для чекпоинтов без видео (оставил вашу логику; прогресс по nested 'progress') ===
  const handleAnswerSubmit = async (cp, answer) => {
    const isCorrect = answer.trim().toLowerCase() === cp.answer.toLowerCase();
    if (isCorrect) {
      setAnsweredCheckpoints(prev => ({ ...prev, [cp.id]: true }));

      try {
        const token = localStorage.getItem('token');
        await fetch(`https://api.eduskill.uz/api/user-kurs/${userKursData.id}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Token ${token}` } : {}),
          },
          body: JSON.stringify({
            progress: {
              ...userKursData.progress,
              [current.id]: {
                ...userKursData.progress?.[current.id],
                checkpoints: {
                  ...(userKursData.progress?.[current.id]?.checkpoints || {}),
                  [cp.id]: true,
                },
                completed: true,
              },
            },
          }),
        });

        setUserKursData(prev => ({
          ...prev,
          progress: {
            ...prev.progress,
            [current.id]: {
              ...(prev.progress?.[current.id] || {}),
              checkpoints: {
                ...(prev.progress?.[current.id]?.checkpoints || {}),
                [cp.id]: true,
              },
              completed: true,
            },
          },
        }));
      } catch (err) {
        console.error("Ошибка сохранения прогресса:", err);
      }
    }
  };

  const allQuestionsAnswered =
    checkpoints.length > 0 &&
    checkpoints.every(cp => answeredCheckpoints[cp.id]);

  const canGoNextElement =
    (!current?.video_uz && allQuestionsAnswered) ||
    (!current?.video_uz && checkpoints.length === 0) ||
    (!!current?.video_uz && (hasProgressAccess || !!completedByElement[current.id])) ||
    (hasProgressAccess);

  const nextDisabledTooltip = (() => {
    if (canGoNextElement) return undefined;
    const msgs = {
      uz: current?.video_uz
        ? "Davom etish uchun videoni to'liq tomosha qiling"
        : "Davom etish uchun barcha savollarga to'g'ri javob bering",
      ru: current?.video_uz
        ? "Досмотрите видео до конца, чтобы продолжить"
        : "Ответьте правильно на все вопросы, чтобы продолжить",
      en: current?.video_uz
        ? "Watch the video to the end to continue"
        : "Answer all questions correctly to continue",
    };
    return msgs[lang] || msgs.uz;
  })();

  // === Навигация ===
  const goToPreviousLesson = () => prevLesson && navigate(`/Lesson/${kursId}/${prevLesson.id}`);
  const goToNextLesson = () => {
    if (!userKursData) return;
    const allowedIndex = userKursData.tartib; // ← было mavzu
    if (nextLesson && nextLesson.tartib <= allowedIndex) {
      navigate(`/Lesson/${kursId}/${nextLesson.id}`);
    }
  };
  const goToFinalLesson = () => {
    navigate(`/test/${kursId}`);
  };
  const goPrevElement = () => currentIndex > 0 && setCurrentIndex(prev => prev - 1);
  const goNextElement = () => currentIndex < elements.length - 1 && setCurrentIndex(prev => prev + 1);

  // === Модалка списка уроков
  const openModal = () => {
    setIsOpen(true);
    setTimeout(() => setIsVisible(true), 10);
    document.body.style.overflow = 'hidden';
  };
  const closeModal = () => {
    setIsVisible(false);
    setTimeout(() => {
      setIsOpen(false);
      document.body.style.overflow = 'auto';
    }, 400);
  };
  const onOverlayClick = (e) => { if (e.target === e.currentTarget) closeModal(); };

  if (loading) return <div className={s.container}>Загрузка...</div>;
  if (!elements.length) return <div className={s.container}>Нет данных</div>;

  return (
    <div className={s.container}>
      {/* модалка */}
      {isOpen && (
        <div className={`${styles.lessonOverlay} ${isVisible ? styles.visible : ''}`} onClick={onOverlayClick}>
          <div className={styles.lessonModal}>
            <div className={styles.lessonModalHeader}>
              <h2>{t('contents')}</h2>
              <button className={styles.lessonCloseBtn} onClick={closeModal}>&times;</button>
            </div>
            <div className={styles.lessonModalContent}>
              <ul>
                {dataThemes.map((item, index) => {
                  const allowedIndex = userKursData?.tartib || 0; // ← было mavzu
                  const isLocked = item.tartib > allowedIndex;
                  return isLocked ? (
                    <li key={index} className={styles.disabledItem}>
                      {handleLang(item.nomi_uz, item.nomi_ru, item.nomi_en)}
                    </li>
                  ) : (
                    <Link to={`/Lesson/${kursId}/${item.id}`} key={index}>
                      <li>{handleLang(item.nomi_uz, item.nomi_ru, item.nomi_en)}</li>
                    </Link>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}

      <section className={s.lesson}>
        {/* шапка */}
        <div className={s.lesson__subheader}>
          <div className={s.lesson__subheaderRight}>
            <div className={s.lesson__subheaderImg} onClick={goToPreviousLesson}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                   className={s.lesson__subheaderImgSvg} style={{ transform: "rotate(180deg)" }}>
                <g>
                  <path className={s.lesson__subheaderImgPath}
                        d="m22.707 11.293-7-7a1 1 0 0 0-1.414 1.414L19.586 11H2a1 1 0 0 0 0 2h17.586l-5.293 5.293a1 1 0 1 0 1.414 1.414l7-7a1 1 0 0 0 0-1.414z"
                        fill="#000"/>
                </g>
              </svg>
            </div>
            <div className={s.lesson__subheaderImg} onClick={openModal}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"
                   className={s.lesson__subheaderImgSvg}>
                <g>
                  <path className={s.lesson__subheaderImgPath}
                        d="M27 10H5a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h22a3 3 0 0 0 3-3V13a3 3 0 0 0-3-3zm1 17a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V13a1 1 0 0 1 1-1h22a1 1 0 0 1 1 1zm-7.55-7.89-8-4A1 1 0 0 0 11 16v8a1 1 0 0 0 .47.85A1 1 0 0 0 12 25a1 1 0 0 0 .45-.11l8-4a1 1 0 0 0 0-1.78zM13 22.38v-4.76L17.76 20zM7 3a1 1 0 0 1 1-1h16a1 1 0 0 1 0 2H8a1 1 0 0 1-1-1zM4 7a1 1 0 0 1 1-1h22a1 1 0 0 1 0 2H5a1 1 0 0 1-1-1z"
                        fill="#000"/>
                </g>
              </svg>
            </div>
            <div className={s.lesson__subheaderImg} onClick={goToNextLesson}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                   className={s.lesson__subheaderImgSvg}>
                <g>
                  <path className={s.lesson__subheaderImgPath}
                        d="m22.707 11.293-7-7a1 1 0 0 0-1.414 1.414L19.586 11H2a1 1 0 0 0 0 2h17.586l-5.293 5.293a1 1 0 1 0 1.414 1.414l7-7a1 1 0 0 0 0-1.414z"
                        fill="#000"/>
                </g>
              </svg>
            </div>
          </div>
          <p className={s.lesson__title}>
            {handleLang(lesson?.nomi_uz, lesson?.nomi_ru, lesson?.nomi_en)} <span>({currentIndex + 1}/{elements.length})</span>
          </p>
        </div>

        {/* контент подтемы */}
        {current?.video_uz && (
          <CustomVideoPlayer
            key={current.id || current.video_uz}
            src={current.video_uz}
            videoId={current.id}
            checkpoints={checkpoints}
            onComplete={handleVideoComplete}
            initialDuration={(() => {
              const raw = current.davomiyligi;
              if (!raw) return 0;
              if (typeof raw === 'number') return raw;
              // Формат "HH:MM:SS" → секунды
              const parts = String(raw).split(':').map(Number);
              if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
              if (parts.length === 2) return parts[0] * 60 + parts[1];
              return parseFloat(raw) || 0;
            })()}
          />
        )}

        {current?.maruza_matn_uz && (
          <div
            className={s.lesson__section}
            dangerouslySetInnerHTML={{
              __html: handleLang(current.maruza_matn_uz, current.maruza_matn_ru, current.maruza_matn_en)
            }}
          />
        )}

        {current?.file_uz && (
          <div className={s.lesson__sectionMaterials}>
            <p className={s.lesson__sectionTitle}>{t('materials')}</p>
            <a href={current.file_uz} target="_blank" rel="noreferrer" download className={s.lesson__file}>
              {t('info')}
            </a>
          </div>
        )}

        {/* если нет видео, но есть вопросы */}
        {!current?.video_uz && checkpoints.length > 0 && (
          <form onSubmit={handleAllAnswersSubmit} className={s.lesson__questions}>
            <h3>
              {t('questions')}
              <h6>{t('questionsSub')}</h6>
            </h3>

            {checkpoints.map(cp => (
              <div key={cp.id} className={s.lesson__questionForm}>
                <p>{cp.question}</p>
                <input
                  type="text"
                  value={userAnswers[cp.id] || ""}
                  onChange={e => handleInputChange(cp.id, e.target.value)}
                  disabled={!!answeredCheckpoints[cp.id]}
                  placeholder={t('enterAnswer')}
                  required
                />

                {answeredCheckpoints[cp.id] === true && (
                  <p className={s.correctAnswerMsg}>{t('correctAnswer2')}</p>
                )}

                {answeredCheckpoints[cp.id] === false && (
                  <p className={s.wrongAnswerMsg}>{t('wrongAnswer')}</p>
                )}
              </div>
            ))}

            <button type="submit" className="register__btn">
              {t('sendAll')}
            </button>
          </form>
        )}

        {/* кнопки навигации */}
        <div className="two__btns">
          <button
            onClick={currentIndex === 0 ? goToPreviousLesson : goPrevElement}
            disabled={currentIndex === 0 && !prevLesson}
            className='register__btn btn__passive'
          >
            {t('prev')}
          </button>

          {currentIndex < elements.length - 1 ? (
            <button onClick={goNextElement} disabled={!canGoNextElement} className='register__btn' title={nextDisabledTooltip}>
              {t('next')}
            </button>
          ) : (
            nextLesson ? (
              <button
                onClick={() => navigate(`/Lesson/${kursId}/${nextLesson.id}`)}
                className='register__btn'
                disabled={!canGoNextElement}
                title={nextDisabledTooltip}
              >
                {t('nextLesson')}
              </button>
            ) : (
              <button onClick={goToFinalLesson} className='register__btn' disabled={!canGoNextElement} title={nextDisabledTooltip}>
                {t('goFinal')}
              </button>
            )
          )}
        </div>
      </section>
    </div>
  );
};
